import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

export interface User {
  customerId: number;
  email: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const TOKEN_KEY = "medipups_auth_token";

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Validate token by calling the backend
  // Client-side JWT decoding without signature validation is insecure
  const validateTokenWithBackend = async (token: string): Promise<User | null> => {
    try {
      const response = await fetch("/api/auth/validate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          customerId: data.customerId,
          email: data.email,
        };
      }

      return null;
    } catch (error) {
      console.error("Error validating token:", error);
      return null;
    }
  };

  // Check if a token looks like a JWT token (has 3 parts) vs magic link token
  const isJWTToken = (token: string): boolean => {
    const parts = token.split('.');
    return parts.length === 3;
  };

  // Unsafe client-side decoding for immediate UI updates
  // Should only be used after backend validation
  const unsafeDecodeToken = (token: string): User | null => {
    try {
      // First check if this is even a JWT token format
      if (!isJWTToken(token)) {
        return null;
      }

      const tokenParts = token.split(".");
      const base64Url = tokenParts[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );

      const payload = JSON.parse(jsonPayload);

      // Check if this looks like a JWT auth token (should have customerId and email)
      if (!payload.customerId || !payload.email) {
        return null;
      }

      // Basic expiry check (not cryptographically secure)
      if (payload.exp && payload.exp < Date.now() / 1000) {
        return null;
      }

      return {
        customerId: payload.customerId,
        email: payload.email,
      };
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = () => {
      setIsLoading(true);

      // Check for token in URL parameters first (from magic link)
      if (typeof window !== "undefined") {
        // Skip token processing if we're on the verify or confirmation page - let those pages handle their tokens
        const isOnVerifyPage = window.location.pathname === '/auth/verify' || window.location.pathname === '/auth/verify/';
        const isOnConfirmationPage = window.location.pathname === '/confirmation' || window.location.pathname === '/confirmation/';
        
        if (isOnVerifyPage || isOnConfirmationPage) {
          setIsLoading(false);
          return;
        }
        
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get("token");

        if (tokenFromUrl) {
          // Check if this is a JWT token or magic link token
          if (!isJWTToken(tokenFromUrl)) {
            // This is a magic link token, redirect to verify endpoint
            window.location.href = `/auth/verify?token=${encodeURIComponent(tokenFromUrl)}`;
            return; // Exit early to prevent further processing
          }
          
          // Additional check: try to decode and see if it has magic link structure
          try {
            const tokenParts = tokenFromUrl.split('.');
            if (tokenParts.length === 3) {
              const base64Url = tokenParts[1];
              const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
              const jsonPayload = decodeURIComponent(
                window.atob(base64).split("").map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
              );
              const testPayload = JSON.parse(jsonPayload);
              
              // If it has 'action' field, it's definitely a magic link token
              if (testPayload.action) {
                window.location.href = `/auth/verify?token=${encodeURIComponent(tokenFromUrl)}`;
                return;
              }
            }
          } catch (e) {
            // If we can't decode it, treat it as invalid
          }
          
          // Token from URL takes priority (JWT token)
          const userData = unsafeDecodeToken(tokenFromUrl);
          if (userData) {
            setToken(tokenFromUrl);
            setUser(userData);
            localStorage.setItem(TOKEN_KEY, tokenFromUrl);

            // Clean up only the token parameter, preserve other URL parameters
            const url = new URL(window.location.href);
            url.searchParams.delete("token");
            window.history.replaceState({}, "", url.toString());
          } else {
            // Invalid JWT token from URL, redirect to login with error
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setUser(null);
            
            const errorMessage = encodeURIComponent("Invalid or expired token");
            window.location.href = `/login?error=${errorMessage}`;
            return;
          }
        } else {
          // Check for stored token
          const storedToken = localStorage.getItem(TOKEN_KEY);
          if (storedToken) {
            // Check if stored token is a JWT token
            if (!isJWTToken(storedToken)) {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
              setUser(null);
            } else {
              const userData = unsafeDecodeToken(storedToken);
              if (userData) {
                setToken(storedToken);
                setUser(userData);
              } else {
                // Stored token is invalid/expired
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
                setUser(null);
              }
            }
          }
        }
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Periodic token validation
  useEffect(() => {
    if (!token || !user) return;

    const validateToken = () => {
      if (token) {
        const userData = unsafeDecodeToken(token);
        if (!userData) {
          logout();
        }
      }
    };

    // Check token validity every 5 minutes
    const interval = setInterval(validateToken, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token, user]);

  const login = (newToken: string) => {
    const userData = unsafeDecodeToken(newToken);
    if (userData) {
      setToken(newToken);
      setUser(userData);
      localStorage.setItem(TOKEN_KEY, newToken);
    } else {
      throw new Error("Invalid token");
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);

    // Redirect to login page
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
